import { create } from 'zustand';

export type EditorMode = 'view' | 'edit' | 'createDrawing' | 'createForm';

export interface EditorSession {
  mode: EditorMode;
  activePolygonData: any | null;
  activeFeature: any | null;
  draft: { id: string; feature: any; coordinates: any } | null;
  popupVisible: boolean;
  /** 轻图测试绘制草稿（缓冲区/停车区/危险区的两步绘制状态） */
  lightMapDraft: LightMapDraft | null;
}

import type { LightMapDraft } from '../utils/lightMap';

interface EditorStore {
  session: EditorSession;
  popupContent: { data: any; mountNode: HTMLElement } | null;

  // Mode 切换 actions
  setBrowseMode: () => void;
  setViewMode: (data: any, feature?: any) => void;
  setEditMode: (data: any) => void;
  setCreateDrawingMode: () => void;
  setCreateFormMode: (
    draft: { id: string; feature: any; coordinates: any },
    lightMapDraft?: LightMapDraft | null,
  ) => void;
  updateActivePolygonData: (data: any | null) => void;

  // 弹窗 actions
  setPopupContent: (content: { data: any; mountNode: HTMLElement } | null) => void;
  clearPolygonPopups: () => void;

  // 轻图测试 actions
  setLightMapDraft: (draft: LightMapDraft | null) => void;
  clearLightMapDraft: () => void;
}

const initialSession: EditorSession = {
  mode: 'view',
  activePolygonData: null,
  activeFeature: null,
  draft: null,
  popupVisible: false,
  lightMapDraft: null,
};

export const useEditorStore = create<EditorStore>((set) => ({
  session: initialSession,
  popupContent: null,

  setBrowseMode: () => set({ session: initialSession }),

  setViewMode: (data, feature) => set((state) => ({
    session: {
      ...state.session,
      mode: 'view',
      activePolygonData: data,
      activeFeature: feature ?? state.session.activeFeature,
      popupVisible: true,
    },
  })),

  setEditMode: (data) => set((state) => ({
    session: {
      ...state.session,
      mode: 'edit',
      activePolygonData: data,
      popupVisible: false,
    },
  })),

  setCreateDrawingMode: () => set({
    session: {
      mode: 'createDrawing',
      activePolygonData: null,
      activeFeature: null,
      draft: null,
      popupVisible: false,
      lightMapDraft: null,
    },
  }),

  setCreateFormMode: (draft, lightMapDraft = null) => set({
    session: {
      mode: 'createForm',
      activePolygonData: null,
      activeFeature: null,
      draft,
      popupVisible: false,
      lightMapDraft,
    },
  }),

  updateActivePolygonData: (data) => set((state) => ({
    session: {
      ...state.session,
      activePolygonData: data,
      popupVisible: data ? state.session.popupVisible : false,
    },
  })),

  setPopupContent: (content) => set({ popupContent: content }),
  clearPolygonPopups: () => set({ popupContent: null }),

  // 轻图测试 actions
  setLightMapDraft: (draft: LightMapDraft | null) => set((state) => ({
    session: {
      ...state.session,
      lightMapDraft: draft,
    },
  })),
  clearLightMapDraft: () => set((state) => ({
    session: {
      ...state.session,
      lightMapDraft: null,
    },
  })),
}));
